interface Point {
  x: number;
  y: number;
}

export const regularPolygon = (sides: number, width: number, height: number): Point[] => {
  const points: Point[] = [];
  const cx = width / 2;
  const cy = height / 2;
  const rx = width / 2;
  const ry = height / 2;
  const startAngle = -Math.PI / 2;
  for (let i = 0; i < sides; i++) {
    const angle = startAngle + (2 * Math.PI * i) / sides;
    points.push({
      x: cx + rx * Math.cos(angle),
      y: cy + ry * Math.sin(angle),
    });
  }
  return points;
};

export const star = (numPoints: number, width: number, height: number, innerRadiusRatio: number = 0.4): Point[] => {
  const vertices: Point[] = [];
  const cx = width / 2;
  const cy = height / 2;
  const outerRx = width / 2;
  const outerRy = height / 2;
  const innerRx = outerRx * innerRadiusRatio;
  const innerRy = outerRy * innerRadiusRatio;
  const startAngle = -Math.PI / 2;
  const totalPoints = numPoints * 2;
  for (let i = 0; i < totalPoints; i++) {
    const angle = startAngle + (2 * Math.PI * i) / totalPoints;
    const isOuter = i % 2 === 0;
    const rx = isOuter ? outerRx : innerRx;
    const ry = isOuter ? outerRy : innerRy;
    vertices.push({
      x: cx + rx * Math.cos(angle),
      y: cy + ry * Math.sin(angle),
    });
  }
  return vertices;
};

export const arrow = (direction: 'right' | 'left' | 'up' | 'down' | 'double', width: number, height: number): Point[] => {
  const shaftRatio = 0.35;
  const headRatio = 0.30;

  const rightArrow = (): Point[] => {
    const shaftTop = height * (0.5 - shaftRatio);
    const shaftBottom = height * (0.5 + shaftRatio);
    const headStart = width * (1 - headRatio);
    return [
      { x: 0, y: shaftTop },
      { x: headStart, y: shaftTop },
      { x: headStart, y: 0 },
      { x: width, y: height / 2 },
      { x: headStart, y: height },
      { x: headStart, y: shaftBottom },
      { x: 0, y: shaftBottom },
    ];
  };

  const leftArrow = (): Point[] => {
    const shaftTop = height * (0.5 - shaftRatio);
    const shaftBottom = height * (0.5 + shaftRatio);
    const headEnd = width * headRatio;
    return [
      { x: 0, y: height / 2 },
      { x: headEnd, y: 0 },
      { x: headEnd, y: shaftTop },
      { x: width, y: shaftTop },
      { x: width, y: shaftBottom },
      { x: headEnd, y: shaftBottom },
      { x: headEnd, y: height },
    ];
  };

  const upArrow = (): Point[] => {
    const shaftLeft = width * (0.5 - shaftRatio);
    const shaftRight = width * (0.5 + shaftRatio);
    const headEnd = height * headRatio;
    return [
      { x: width / 2, y: 0 },
      { x: width, y: headEnd },
      { x: shaftRight, y: headEnd },
      { x: shaftRight, y: height },
      { x: shaftLeft, y: height },
      { x: shaftLeft, y: headEnd },
      { x: 0, y: headEnd },
    ];
  };

  const downArrow = (): Point[] => {
    const shaftLeft = width * (0.5 - shaftRatio);
    const shaftRight = width * (0.5 + shaftRatio);
    const headStart = height * (1 - headRatio);
    return [
      { x: shaftLeft, y: 0 },
      { x: shaftRight, y: 0 },
      { x: shaftRight, y: headStart },
      { x: width, y: headStart },
      { x: width / 2, y: height },
      { x: 0, y: headStart },
      { x: shaftLeft, y: headStart },
    ];
  };

  const doubleArrow = (): Point[] => {
    const shaftTop = height * (0.5 - shaftRatio);
    const shaftBottom = height * (0.5 + shaftRatio);
    const headLen = width * headRatio;
    return [
      { x: 0, y: height / 2 },
      { x: headLen, y: 0 },
      { x: headLen, y: shaftTop },
      { x: width - headLen, y: shaftTop },
      { x: width - headLen, y: 0 },
      { x: width, y: height / 2 },
      { x: width - headLen, y: height },
      { x: width - headLen, y: shaftBottom },
      { x: headLen, y: shaftBottom },
      { x: headLen, y: height },
    ];
  };

  switch (direction) {
    case 'right': return rightArrow();
    case 'left': return leftArrow();
    case 'up': return upArrow();
    case 'down': return downArrow();
    case 'double': return doubleArrow();
  }
};

export const callout = (width: number, height: number): Point[] => {
  const bodyHeight = height * 0.75;
  const tailWidth = width * 0.15;
  const tailStartX = width * 0.15;
  const tailTipX = width * 0.05;
  const cornerRadius = Math.min(width, bodyHeight) * 0.08;
  return [
    { x: cornerRadius, y: 0 },
    { x: width - cornerRadius, y: 0 },
    { x: width, y: cornerRadius },
    { x: width, y: bodyHeight - cornerRadius },
    { x: width - cornerRadius, y: bodyHeight },
    { x: tailStartX + tailWidth, y: bodyHeight },
    { x: tailTipX, y: height },
    { x: tailStartX, y: bodyHeight },
    { x: cornerRadius, y: bodyHeight },
    { x: 0, y: bodyHeight - cornerRadius },
    { x: 0, y: cornerRadius },
  ];
};

export const banner = (width: number, height: number): Point[] => {
  const foldDepth = width * 0.08;
  const foldHeight = height * 0.15;
  return [
    { x: foldDepth, y: 0 },
    { x: width - foldDepth, y: 0 },
    { x: width, y: foldHeight },
    { x: width - foldDepth, y: foldHeight },
    { x: width - foldDepth, y: height - foldHeight },
    { x: width, y: height - foldHeight },
    { x: width - foldDepth, y: height },
    { x: foldDepth, y: height },
    { x: 0, y: height - foldHeight },
    { x: foldDepth, y: height - foldHeight },
    { x: foldDepth, y: foldHeight },
    { x: 0, y: foldHeight },
  ];
};

export const getShapePoints = (
  shapeType: string,
  width: number,
  height: number
): Point[] | null => {
  switch (shapeType) {
    case 'triangle': return regularPolygon(3, width, height);
    case 'pentagon': return regularPolygon(5, width, height);
    case 'hexagon': return regularPolygon(6, width, height);
    case 'octagon': return regularPolygon(8, width, height);
    case 'star-5': return star(5, width, height, 0.38);
    case 'star-6': return star(6, width, height, 0.45);
    case 'star-8': return star(8, width, height, 0.5);
    case 'arrow-right': return arrow('right', width, height);
    case 'arrow-left': return arrow('left', width, height);
    case 'arrow-up': return arrow('up', width, height);
    case 'arrow-down': return arrow('down', width, height);
    case 'arrow-double': return arrow('double', width, height);
    case 'callout': return callout(width, height);
    case 'banner': return banner(width, height);
    default: return null;
  }
};

export interface ShapeDefinition {
  type: string;
  label: string;
  category: 'basic' | 'polygon' | 'star' | 'arrow' | 'special';
}

export const SHAPE_LIBRARY: ShapeDefinition[] = [
  { type: 'rectangle', label: 'Rectangle', category: 'basic' },
  { type: 'rounded-rect', label: 'Rounded Rect', category: 'basic' },
  { type: 'circle', label: 'Circle', category: 'basic' },
  { type: 'triangle', label: 'Triangle', category: 'polygon' },
  { type: 'pentagon', label: 'Pentagon', category: 'polygon' },
  { type: 'hexagon', label: 'Hexagon', category: 'polygon' },
  { type: 'octagon', label: 'Octagon', category: 'polygon' },
  { type: 'star-5', label: '5-Point Star', category: 'star' },
  { type: 'star-6', label: '6-Point Star', category: 'star' },
  { type: 'star-8', label: '8-Point Star', category: 'star' },
  { type: 'arrow-right', label: 'Right Arrow', category: 'arrow' },
  { type: 'arrow-left', label: 'Left Arrow', category: 'arrow' },
  { type: 'arrow-up', label: 'Up Arrow', category: 'arrow' },
  { type: 'arrow-down', label: 'Down Arrow', category: 'arrow' },
  { type: 'arrow-double', label: 'Double Arrow', category: 'arrow' },
  { type: 'callout', label: 'Callout', category: 'special' },
  { type: 'banner', label: 'Banner', category: 'special' },
];
